import {
  useAuthorshipsByPeriod,
  useCourseCertificatesByPeriod,
  useLegalAidsByPeriod,
  useReductionsByPeriod,
  useTeachingsByPeriod,
} from "@/hooks/cle-hooks";

import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
} from "../../../../accordion/Accordion";
import { AuthorshipsTable } from "./AuthorshipsTable";
import { CourseCertificatesTable } from "./CourseCertificatesTable";
import { LegalAidTable } from "./LegalAidTable";
import { ReductionsTable } from "./ReductionsTable";
import { TeachingsTable } from "./TeachingsTable";
import type { MockPagination } from "./types";

interface ReportDocumentAccordionsProps {
  userId: string;
  periodId: string;
  onSelectCertificate: (documentId: string) => void;
  onSelectReduction: (reductionId: string) => void;
  onSelectLegalAid: (legalAidId: string) => void;
  onSelectAuthorship: (authorshipId: string) => void;
  onSelectTeaching: (teachingId: string) => void;
}

const buildListPagination = (count: number): MockPagination => ({
  page: 1,
  pageSize: Math.max(count, 1),
  totalCount: count,
  hasPreviousPage: false,
  hasNextPage: false,
});

export const ReportDocumentAccordions = ({
  userId,
  periodId,
  onSelectCertificate,
  onSelectReduction,
  onSelectLegalAid,
  onSelectAuthorship,
  onSelectTeaching,
}: ReportDocumentAccordionsProps) => {
  const {
    data: courseCertificates = [],
    isLoading: isCourseCertificatesLoading,
    error: courseCertificatesError,
  } = useCourseCertificatesByPeriod({ userId, periodId });
  const {
    data: reductions = [],
    isLoading: isReductionsLoading,
    error: reductionsError,
  } = useReductionsByPeriod({ userId, periodId });
  const {
    data: legalAids = [],
    isLoading: isLegalAidsLoading,
    error: legalAidsError,
  } = useLegalAidsByPeriod({ userId, periodId });
  const {
    data: authorships = [],
    isLoading: isAuthorshipsLoading,
    error: authorshipsError,
  } = useAuthorshipsByPeriod({ userId, periodId });
  const {
    data: teachings = [],
    isLoading: isTeachingsLoading,
    error: teachingsError,
  } = useTeachingsByPeriod({ userId, periodId });

  const sections = [
    {
      id: "kursusbeviser",
      title: "Kursusbeviser",
      count: courseCertificates.length,
      content: (
        <CourseCertificatesTable
          certificates={courseCertificates}
          isLoading={isCourseCertificatesLoading}
          error={courseCertificatesError}
          onSelectCertificate={onSelectCertificate}
          pagination={buildListPagination(courseCertificates.length)}
          showPagination={false}
        />
      ),
    },
    {
      id: "reduktioner",
      title: "Reduktioner",
      count: reductions.length,
      content: (
        <ReductionsTable
          reductions={reductions}
          isLoading={isReductionsLoading}
          error={reductionsError}
          onSelectReduction={onSelectReduction}
          pagination={buildListPagination(reductions.length)}
          showPagination={false}
        />
      ),
    },
    {
      id: "advokatvagt-retshjaelp",
      title: "Advokatvagt/retshjælp",
      count: legalAids.length,
      content: (
        <LegalAidTable
          legalAids={legalAids}
          isLoading={isLegalAidsLoading}
          error={legalAidsError}
          onSelectLegalAid={onSelectLegalAid}
          pagination={buildListPagination(legalAids.length)}
          showPagination={false}
        />
      ),
    },
    {
      id: "forfatterskab",
      title: "Forfatterskab",
      count: authorships.length,
      content: (
        <AuthorshipsTable
          authorships={authorships}
          isLoading={isAuthorshipsLoading}
          error={authorshipsError}
          onSelectAuthorship={onSelectAuthorship}
          pagination={buildListPagination(authorships.length)}
          showPagination={false}
        />
      ),
    },
    {
      id: "undervisning",
      title: "Undervisning",
      count: teachings.length,
      content: (
        <TeachingsTable
          teachings={teachings}
          isLoading={isTeachingsLoading}
          error={teachingsError}
          onSelectTeaching={onSelectTeaching}
          pagination={buildListPagination(teachings.length)}
          showPagination={false}
        />
      ),
    },
  ];

  return (
    <AccordionRoot className="mt-4">
      {sections.map((section) => (
        <AccordionItem
          className="c-card c-card--accordion"
          value={section.id}
          isEmpty={!section.count}
          key={section.id}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>
                {section.title} ({section.count})
              </span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>{section.content}</AccordionContent>
        </AccordionItem>
      ))}
    </AccordionRoot>
  );
};
